import {
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { createPortal } from "react-dom";

import type {
  DayCellMountArg,
  EventClickArg,
  EventDropArg,
} from "@fullcalendar/core";
import enGbLocale from "@fullcalendar/core/locales/en-gb";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import type {
  DateClickArg,
  EventResizeDoneArg,
} from "@fullcalendar/interaction";
import listPlugin from "@fullcalendar/list";
import FullCalendar from "@fullcalendar/react";
import timeGridPlugin from "@fullcalendar/timegrid";
import { isBefore, startOfDay } from "date-fns";

import { formatDate } from "simplydo/core/dates";
import { RichTextViewer } from "simplydo/react/richtext";
import { cx, styled } from "simplydo/react/styled";
import { Button, Dropdown, Modal, Table } from "simplydo/react/ui";
import type { DropdownProps } from "simplydo/react/ui";

import { useAppSelector } from "store";
import util from "utils/utils";

import { UserChip } from "components/lib/Chips";

import {
  CalendarEventWithDates,
  CalendarResponseEvent,
  CellAction,
} from "./calendar-types";
import {
  allDayExclusiveEndToInclusive,
  fromAllDayStorageDate,
  getDefaultActionDate,
  getDefaultEventEndDate,
  isSameAllDayDate,
  toAllDayStorageDate,
  toFullCalendarEvents,
} from "./fullCalendarUtils";

const CALENDAR_VIEW_OPTIONS = [
  { key: "dayGridMonth", text: "Month", value: "dayGridMonth" },
  { key: "timeGridWeek", text: "Week", value: "timeGridWeek" },
  { key: "timeGridDay", text: "Day", value: "timeGridDay" },
  { key: "listMonth", text: "Month list", value: "listMonth" },
] as const;

type CalendarViewType = (typeof CALENDAR_VIEW_OPTIONS)[number]["value"];

const CalendarWrapper = styled.div`
  position: relative;
  overflow: visible;
  .fc {
    --fc-border-color: #e0e0e0;
    --fc-today-bg-color: transparent;
    --fc-event-border-color: transparent;
    --fc-neutral-text-color: rgba(0, 0, 0, 0.45);
    --fc-button-bg-color: ${({ theme }) => theme.primaryColour};
    --fc-button-border-color: ${({ theme }) => theme.primaryColour};
    --fc-button-hover-bg-color: ${({ theme }) =>
      util.adjustColour(theme.primaryColour, -0.08)};
    --fc-button-hover-border-color: ${({ theme }) =>
      util.adjustColour(theme.primaryColour, -0.08)};
    --fc-button-active-bg-color: ${({ theme }) =>
      util.adjustColour(theme.primaryColour, -0.12)};
    --fc-button-active-border-color: ${({ theme }) =>
      util.adjustColour(theme.primaryColour, -0.12)};
    color: #000;
    font-size: 13px;
  }

  .fc .fc-col-header-cell-cushion,
  .fc .fc-daygrid-day-number,
  .fc .fc-toolbar-title,
  .fc .fc-timegrid-axis-cushion,
  .fc .fc-timegrid-slot-label-cushion {
    color: #000;
  }

  .fc .fc-day-other .fc-daygrid-day-number {
    color: rgba(0, 0, 0, 0.4);
  }

  .fc .fc-daygrid-day-frame {
    min-height: 8rem;
    height: 100%;
    box-sizing: border-box;
  }

  .fc .fc-daygrid-day.fc-day-today,
  .fc .fc-timegrid-col.fc-day-today {
    background-color: transparent;
  }

  .fc .fc-daygrid-day .fc-daygrid-day-number {
    border-radius: 20px;
    width: 20px;
    height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
  }

  .fc .fc-daygrid-day.fc-day-today .fc-daygrid-day-number {
    background: #0b57d0;
    font-weight: 600;
    color: #fff;
  }

  .fc .fc-col-header-cell.fc-day-today {
    background: #e6f7ff;
  }

  .fc .fc-toolbar {
    flex-wrap: wrap;
    gap: 12px;
    margin-bottom: 12px;
  }

  .fc .fc-toolbar-chunk {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  .fc .fc-toolbar-chunk:first-child {
    gap: 8px;
  }

  .fc .fc-toolbar-title {
    font-size: 1.25em;
    font-weight: 600;
    margin: 0;
    padding: 0 4px;
  }

  .fc .fc-toolbar-chunk:first-child .fc-today-button {
    background: #fff;
    color: ${({ theme }) => theme.primaryColour};
    border: 1px solid ${({ theme }) => theme.primaryColour};
    border-radius: 4px;
    font-weight: 600;
    text-transform: capitalize;
  }

  .fc .fc-toolbar-chunk:first-child .fc-today-button:hover {
    background: #f0f7fc;
    border-color: ${({ theme }) => theme.primaryColour};
    color: ${({ theme }) => theme.primaryColour};
  }

  .fc .fc-toolbar-chunk:first-child .fc-prev-button,
  .fc .fc-toolbar-chunk:first-child .fc-next-button {
    background: transparent;
    border: none;
    color: #000;
    box-shadow: none;
    padding: 0.35em 0.5em;
    min-width: 2em;
  }

  .fc .fc-toolbar-chunk:first-child .fc-prev-button:hover,
  .fc .fc-toolbar-chunk:first-child .fc-next-button:hover {
    background: rgba(0, 0, 0, 0.06);
    border: none;
    color: #000;
  }

  .fc .fc-toolbar-chunk:first-child .fc-button-group {
    gap: 0;
  }

  .fc .fc-toolbar-chunk:first-child .fc-button-group .fc-button {
    border-radius: 4px;
  }

  .fc .fc-button {
    background: ${({ theme }) => theme.primaryColour};
    border-color: ${({ theme }) => theme.primaryColour};
    font-size: 0.9em;
    padding: 0.4em 0.65em;
  }

  .fc .fc-button:hover {
    background: ${({ theme }) => util.adjustColour(theme.primaryColour, -0.08)};
    border-color: ${({ theme }) =>
      util.adjustColour(theme.primaryColour, -0.08)};
  }

  .fc .fc-button-primary:not(:disabled).fc-button-active,
  .fc .fc-button-primary:not(:disabled):active {
    background: ${({ theme }) => util.adjustColour(theme.primaryColour, -0.12)};
    border-color: ${({ theme }) =>
      util.adjustColour(theme.primaryColour, -0.12)};
  }

  .fc .fc-daygrid-event {
    cursor: pointer;
  }

  .fc .fc-daygrid-block-event {
    padding-left: 3px;
  }

  .fc .fc-daygrid-dot-event.fc-event-start,
  .fc .fc-daygrid-block-event.fc-event-start {
    border-top-left-radius: 6px;
    border-bottom-left-radius: 6px;
  }

  .fc .fc-daygrid-dot-event.fc-event-start,
  .fc .fc-daygrid-block-event.fc-event-end {
    border-top-right-radius: 6px;
    border-bottom-right-radius: 6px;
  }

  .fc .fc-daygrid-event.fc-daygrid-block-event .fc-event-time {
    margin-right: 1px;
  }

  .fc .fc-daygrid-block-event:not(.fc-event-start) .fc-event-time,
  .fc .fc-daygrid-dot-event:not(.fc-event-start) .fc-event-time {
    display: none;
  }

  .fc .fc-daygrid-event-harness:not(.fc-daygrid-event-harness-abs) {
    max-width: 100%;
  }

  .fc .fc-daygrid-block-event,
  .fc .fc-daygrid-dot-event {
    line-height: 1.5em;
  }

  .fc .fc-daygrid-dot-event {
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    color: #000;
  }

  .fc .fc-daygrid-dot-event .fc-event-title,
  .fc .fc-daygrid-block-event .fc-event-title {
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .fc .fc-daygrid-block-event .fc-event-title {
    font-weight: 600;
  }

  .fc .fc-v-event .fc-event-title {
    text-overflow: ellipsis;
    white-space: nowrap;
    font-weight: 600;
  }

  .fc .fc-bg-event {
    opacity: 0.25;
  }

  &.is-editable {
    .fc-daygrid-day-frame {
      position: relative;
      cursor: pointer;
      transition: background-color 0.15s ease;
      --fc-day-add-button-reserve: 15px;
      padding-bottom: var(--fc-day-add-button-reserve);
      box-sizing: border-box;
    }

    .fc .fc-daygrid-body-balanced .fc-daygrid-day-events {
      bottom: var(--fc-day-add-button-reserve);
    }

    .fc .fc-daygrid-body-unbalanced .fc-daygrid-day-events {
      margin-bottom: var(--fc-day-add-button-reserve);
    }

    .fc .fc-daygrid-day:hover,
    .fc .fc-timegrid-slot:hover {
      background-color: #f0f7fcaa;
    }

    .fc .fc-timegrid-body {
      cursor: pointer;
    }

    .fc-timegrid-cell-hover {
      position: absolute;
      display: none;
      background-color: #f0f7fc;
      pointer-events: none;
      z-index: 1;
      box-sizing: border-box;
    }

    .fc-day-add-button {
      position: absolute;
      bottom: 4px;
      right: 4px;
      z-index: 3;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 24px;
      height: 24px;
      padding: 0;
      border: 1px solid ${({ theme }) => theme.primaryColour};
      border-radius: 4px;
      background: #fff;
      color: ${({ theme }) => theme.primaryColour};
      font-size: 16px;
      font-weight: bold;
      line-height: 1;
      cursor: pointer;
      opacity: 0;
      pointer-events: none;
      transition:
        opacity 0.15s ease,
        background-color 0.15s ease;
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
    }

    .fc-daygrid-day-frame:hover .fc-day-add-button,
    .fc-day-add-button.is-open,
    .fc-day-add-button:focus-visible {
      opacity: 1;
      pointer-events: auto;
    }

    .fc-day-add-button:hover {
      background: ${({ theme }) => theme.primaryColour};
      color: #fff;
      border-color: ${({ theme }) => theme.primaryColour};
    }
  }
`;

const ToolbarControls = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  z-index: 10;
  display: flex;
  align-items: center;
  gap: 8px;

  .ui.selection.dropdown {
    min-width: 10em;
    font-size: 0.9em;
    font-weight: normal;
  }

  .ui.dropdown.active,
  .ui.dropdown.visible {
    z-index: 11;
  }

  .ui.dropdown .menu {
    z-index: 12;
    background: #fff;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
  }
`;

type DayActionMenuState = {
  date: Date;
  useClickedTime?: boolean;
  anchor?: HTMLElement;
  position?: { top: number; left: number };
};

const DAY_ACTION_MENU_WIDTH = 220;

const getDayActionMenuPosition = (menu: DayActionMenuState) => {
  if (menu.position) {
    return {
      top: menu.position.top + 4,
      left: Math.max(
        8,
        Math.min(
          menu.position.left - DAY_ACTION_MENU_WIDTH / 2,
          window.innerWidth - DAY_ACTION_MENU_WIDTH - 8,
        ),
      ),
    };
  }

  if (menu.anchor) {
    const rect = menu.anchor.getBoundingClientRect();
    return {
      top: rect.bottom + 4,
      left: Math.max(8, rect.right - DAY_ACTION_MENU_WIDTH),
    };
  }

  return null;
};

const DayActionMenu = styled.div<{ $top: number; $left: number }>`
  position: fixed;
  top: ${({ $top }) => $top}px;
  left: ${({ $left }) => $left}px;
  z-index: 1000;
  min-width: 220px;
  background: #fff;
  border: 1px solid rgba(34, 36, 38, 0.15);
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
  padding: 4px 0;
`;

const DayActionMenuItem = styled.button`
  display: block;
  width: 100%;
  padding: 10px 16px;
  border: none;
  background: none;
  text-align: left;
  cursor: pointer;
  font-size: 14px;
  font-weight: normal;
  color: #000;

  &:hover {
    background: rgba(0, 0, 0, 0.05);
  }
`;

type FullCalendarViewProps = {
  events: CalendarResponseEvent[];
  updateEvent: (event: CalendarResponseEvent) => void;
  deleteEvent?: (eventId: string, eventType: string) => void;
  leaveEvent?: (eventId: string) => void;
  setEditingEvent: Dispatch<
    SetStateAction<Partial<CalendarEventWithDates> | null>
  >;
  editable: boolean;
  showMembers: boolean;
  cellActions?: Array<CellAction>;
  toolbarExtra?: ReactNode;
};

const DIRECT_EDIT_TYPES = new Set([
  "announcement",
  "open",
  "deadline",
  "assessmentDeadline",
]);

const FullCalendarView = ({
  events,
  updateEvent,
  deleteEvent,
  leaveEvent,
  setEditingEvent,
  editable,
  showMembers,
  cellActions,
  toolbarExtra,
}: FullCalendarViewProps) => {
  const user = useAppSelector((state) => state.user);
  const calendarRef = useRef<FullCalendar>(null);
  const calendarWrapperRef = useRef<HTMLDivElement>(null);
  const [currentView, setCurrentView] =
    useState<CalendarViewType>("dayGridMonth");
  const [viewingEvent, setViewingEvent] =
    useState<CalendarResponseEvent | null>(null);
  const [dayActionMenu, setDayActionMenu] = useState<DayActionMenuState | null>(
    null,
  );
  const dayActionMenuRef = useRef<HTMLDivElement>(null);

  const fcEvents = useMemo(
    () => toFullCalendarEvents(events, editable),
    [events, editable],
  );

  const handleEventClick = (info: EventClickArg) => {
    if (info.event.display === "background") {
      return;
    }
    const sourceEvent = info.event.extendedProps.sourceEvent as
      | CalendarResponseEvent
      | undefined;
    if (!sourceEvent) {
      return;
    }
    if (sourceEvent.type && DIRECT_EDIT_TYPES.has(sourceEvent.type)) {
      setEditingEvent(sourceEvent as Partial<CalendarEventWithDates>);
      return;
    }
    setViewingEvent(sourceEvent);
  };

  const handleDateClick = (info: DateClickArg) => {
    if (!editable || info.view.type === "dayGridMonth") {
      return;
    }
    openDayActionMenu(info.date, {
      position: { top: info.jsEvent.clientY, left: info.jsEvent.clientX },
      useClickedTime: true,
    });
  };

  const openDayActionMenu = useCallback(
    (
      date: Date,
      options: {
        anchor?: HTMLElement;
        position?: { top: number; left: number };
        useClickedTime?: boolean;
      },
    ) => {
      setDayActionMenu((prev) => {
        if (options.anchor && prev?.anchor === options.anchor) {
          options.anchor.classList.remove("is-open");
          return null;
        }
        prev?.anchor?.classList.remove("is-open");
        options.anchor?.classList.add("is-open");
        return { date, ...options };
      });
    },
    [],
  );

  const closeDayActionMenu = useCallback(() => {
    setDayActionMenu((prev) => {
      prev?.anchor?.classList.remove("is-open");
      return null;
    });
  }, []);

  useEffect(() => {
    if (!dayActionMenu) {
      return;
    }

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        (dayActionMenu.anchor && dayActionMenu.anchor.contains(target)) ||
        dayActionMenuRef.current?.contains(target)
      ) {
        return;
      }
      closeDayActionMenu();
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closeDayActionMenu();
      }
    };

    const handleScroll = () => {
      closeDayActionMenu();
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleEscape);
    document.addEventListener("scroll", handleScroll, true);
    window.addEventListener("scroll", handleScroll, true);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleEscape);
      document.removeEventListener("scroll", handleScroll, true);
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [closeDayActionMenu, dayActionMenu]);

  const handleDayCellDidMount = useCallback(
    (info: DayCellMountArg) => {
      if (!editable) {
        return;
      }

      const frame =
        info.el.querySelector<HTMLElement>(".fc-daygrid-day-frame") ?? info.el;
      if (frame.querySelector(".fc-day-add-button")) {
        return;
      }

      const addButton = document.createElement("button");
      addButton.type = "button";
      addButton.className = "fc-day-add-button";
      addButton.setAttribute("aria-label", "Add to calendar");
      addButton.title = "Add to calendar";
      addButton.textContent = "+";
      addButton.addEventListener("click", (event) => {
        event.stopPropagation();
        openDayActionMenu(info.date, { anchor: addButton });
      });
      frame.appendChild(addButton);
    },
    [editable, openDayActionMenu],
  );

  const handleDayCellWillUnmount = useCallback((info: DayCellMountArg) => {
    info.el.querySelector(".fc-day-add-button")?.remove();
  }, []);

  const dayCellClassNames = useCallback(() => {
    return editable ? ["fc-day-clickable"] : [];
  }, [editable]);

  const applyDateUpdate = (
    sourceEvent: CalendarResponseEvent,
    start: Date | null,
    end: Date | null | undefined,
    allDay: boolean,
  ) => {
    if (allDay && start) {
      const inclusiveEnd = end
        ? allDayExclusiveEndToInclusive(end)
        : toAllDayStorageDate(start);
      updateEvent({
        ...sourceEvent,
        allDay: true,
        from: toAllDayStorageDate(start).toISOString(),
        to: inclusiveEnd.toISOString(),
      });
      return;
    }
    updateEvent({
      ...sourceEvent,
      allDay: false,
      from: start?.toISOString() ?? sourceEvent.from,
      to: end?.toISOString() ?? sourceEvent.to,
    });
  };

  const handleEventDrop = (info: EventDropArg) => {
    const sourceEvent = info.event.extendedProps.sourceEvent as
      | CalendarResponseEvent
      | undefined;
    if (!sourceEvent) {
      info.revert();
      return;
    }
    applyDateUpdate(
      sourceEvent,
      info.event.start,
      info.event.end,
      info.event.allDay,
    );
  };

  const handleEventResize = (info: EventResizeDoneArg) => {
    const sourceEvent = info.event.extendedProps.sourceEvent as
      | CalendarResponseEvent
      | undefined;
    if (!sourceEvent) {
      info.revert();
      return;
    }
    applyDateUpdate(
      sourceEvent,
      info.event.start,
      info.event.end,
      info.event.allDay,
    );
  };

  const createEventAtDate = (dayDate: Date, useClickedTime = false) => {
    if (!user) {
      return;
    }
    closeDayActionMenu();
    const from = getDefaultActionDate(dayDate, useClickedTime);
    setEditingEvent({
      type: "event",
      from,
      to: getDefaultEventEndDate(from),
      color: "#3f88d2",
      members: [user._id],
      ownerMembers: [{ _id: user._id, profile: user.profile }],
      organiserName: user.profile?.fullName ?? "",
      organiserEmail: user.emails[0]?.address ?? "",
      notifications: [{ value: 10, unit: "minutes" }],
    });
  };

  const viewingEventFrom = viewingEvent?.from
    ? new Date(viewingEvent.from)
    : null;
  const viewingEventTo = viewingEvent?.to
    ? new Date(viewingEvent.to)
    : viewingEventFrom;
  const isViewingAllDay = !!viewingEvent?.allDay;
  const hasSeparateEndDate =
    viewingEventFrom &&
    viewingEventTo &&
    (isViewingAllDay
      ? !isSameAllDayDate(viewingEventFrom, viewingEventTo)
      : viewingEventFrom.toDateString() !== viewingEventTo.toDateString());
  const dateFormat = isViewingAllDay ? "MMM dd, yyyy" : "MMM dd, HH:mm";
  const formatViewingDate = (date: Date) => {
    if (isViewingAllDay) {
      return formatDate(fromAllDayStorageDate(date), "MMM dd, yyyy");
    }
    return formatDate(date, dateFormat);
  };

  const handleViewChange = (
    _event: React.SyntheticEvent<HTMLElement>,
    data: DropdownProps,
  ) => {
    if (typeof data.value !== "string") {
      return;
    }
    const view = data.value as CalendarViewType;
    setCurrentView(view);
    calendarRef.current?.getApi().changeView(view);
  };

  const visibleCellActions = useMemo(() => {
    if (!dayActionMenu || !cellActions) {
      return [];
    }
    return cellActions.filter((action) => {
      if (!action.onlyVisibleInRange) {
        return true;
      }
      return !isBefore(startOfDay(dayActionMenu.date), startOfDay(new Date()));
    });
  }, [cellActions, dayActionMenu]);

  const dayActionMenuPosition = dayActionMenu
    ? getDayActionMenuPosition(dayActionMenu)
    : null;

  return (
    <CalendarWrapper
      ref={calendarWrapperRef}
      className={cx(editable && "is-editable")}
    >
      <ToolbarControls>
        {toolbarExtra}
        <Dropdown
          selection
          options={[...CALENDAR_VIEW_OPTIONS]}
          value={currentView}
          onChange={handleViewChange}
          aria-label="Calendar view"
        />
      </ToolbarControls>
      <FullCalendar
        ref={calendarRef}
        plugins={[dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin]}
        locale={enGbLocale}
        initialView="dayGridMonth"
        firstDay={1}
        headerToolbar={{ left: "today prev,next title", center: "", right: "" }}
        buttonText={{ today: "Today" }}
        eventTimeFormat={{
          hour: "numeric",
          minute: "2-digit",
          meridiem: false,
          hour12: false,
        }}
        events={fcEvents}
        editable={editable}
        eventStartEditable={editable}
        eventDurationEditable={editable}
        fixedWeekCount={false}
        height="auto"
        views={{
          dayGridMonth: { aspectRatio: 1 },
          timeGridWeek: {
            height: 600,
            dayHeaderFormat: {
              weekday: "short",
              day: "numeric",
              month: "numeric",
              omitCommas: true,
            },
          },
          timeGridDay: {
            height: 600,
            dayHeaderFormat: {
              weekday: "short",
              day: "numeric",
              month: "numeric",
              omitCommas: true,
            },
          },
        }}
        nowIndicator
        dayCellClassNames={dayCellClassNames}
        dayCellDidMount={handleDayCellDidMount}
        dayCellWillUnmount={handleDayCellWillUnmount}
        dateClick={handleDateClick}
        eventClick={handleEventClick}
        eventDrop={handleEventDrop}
        eventResize={handleEventResize}
        dayMaxEvents={4}
        moreLinkClick="popover"
      />

      {dayActionMenu && dayActionMenuPosition
        ? createPortal(
            <DayActionMenu
              ref={dayActionMenuRef}
              $top={dayActionMenuPosition.top}
              $left={dayActionMenuPosition.left}
              role="menu"
              aria-label="Add to calendar"
            >
              <DayActionMenuItem
                type="button"
                role="menuitem"
                onClick={() =>
                  createEventAtDate(
                    dayActionMenu.date,
                    !!dayActionMenu.useClickedTime,
                  )
                }
              >
                Add an event
              </DayActionMenuItem>
              {visibleCellActions.map((action) => (
                <DayActionMenuItem
                  key={action.name}
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    const eventData = action.action(
                      getDefaultActionDate(
                        dayActionMenu.date,
                        !!dayActionMenu.useClickedTime,
                      ).toISOString(),
                    );
                    closeDayActionMenu();
                    setEditingEvent(
                      eventData as Partial<CalendarEventWithDates>,
                    );
                  }}
                >
                  {action.name}
                </DayActionMenuItem>
              ))}
            </DayActionMenu>,
            document.body,
          )
        : null}

      <Modal
        mountNode={document.getElementById("semantic-modal-mount-node")}
        open={!!viewingEvent}
        onClose={() => setViewingEvent(null)}
      >
        <Modal.Header>{viewingEvent?.title}</Modal.Header>
        <Modal.Content scrolling>
          {viewingEventFrom ? (
            <>
              <h4>Date</h4>
              {hasSeparateEndDate
                ? `${formatViewingDate(viewingEventFrom)} - ${formatViewingDate(viewingEventTo!)}`
                : formatViewingDate(viewingEventFrom)}
            </>
          ) : null}
          {viewingEvent?.description ? (
            <>
              <h4>Description</h4>
              <RichTextViewer value={viewingEvent.description} />
            </>
          ) : null}
          {showMembers &&
          viewingEvent?.ownerMembers &&
          viewingEvent.ownerMembers.length > 0 ? (
            <Table>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>Members</Table.HeaderCell>
                  <Table.HeaderCell />
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {viewingEvent.ownerMembers.map((member) => (
                  <Table.Row key={member._id}>
                    <Table.Cell>
                      <UserChip user={member as never} compact />
                    </Table.Cell>
                    <Table.Cell collapsing>
                      {leaveEvent && member._id === user?._id ? (
                        <Button
                          content="Leave"
                          icon="sign-out"
                          basic
                          onClick={() => {
                            leaveEvent(viewingEvent.id!);
                            setViewingEvent(null);
                          }}
                        />
                      ) : null}
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          ) : null}
        </Modal.Content>
        <Modal.Actions>
          {editable && viewingEvent && !viewingEvent.isGenerated ? (
            <>
              {deleteEvent && viewingEvent.id ? (
                <Button
                  icon="trash"
                  basic
                  onClick={() => {
                    deleteEvent(viewingEvent.id!, viewingEvent.type || "event");
                    setViewingEvent(null);
                  }}
                />
              ) : null}
              <Button
                content="Edit"
                icon="edit"
                onClick={() => {
                  setEditingEvent(
                    viewingEvent as Partial<CalendarEventWithDates>,
                  );
                  setViewingEvent(null);
                }}
              />
            </>
          ) : null}
          <Button color="black" onClick={() => setViewingEvent(null)}>
            Close
          </Button>
        </Modal.Actions>
      </Modal>
    </CalendarWrapper>
  );
};

export default FullCalendarView;
